import React, { useEffect, useRef, useState } from 'react';
import {
    GOOGLE_ADDRESS_REGION_CODES, GooglePlacePrediction, SelectedGoogleAddress,
    isGoogleAddressAutocompleteConfigured, loadGoogleAddressPlaces, mapGoogleAddress,
} from './googleAddressAutocomplete';

// Claude Design "CMP Accelerator Member Dashboard (2)": Company Address and
// Billing/Mailing Address street search (ACC2:250-275, 404-432) and the
// global Country dropdown (CMP Dashboard (20) ds-components/country-select.js).
// Suggestions come from Google Places (Canada and United States, as in the
// staff dashboard and website); the popup follows the design.

const GOLD = '#E1A731';
const TEXT = '#000000';
const BORDER = '#9E9E9E';
const GRAY_600 = '#727272';
const RED = '#ED1C24';
const HIGHLIGHT = '#FBF4E4';
const SELECTED = '#FDF8EC';
export const ADDRESS_SEARCH_MIN_CHARACTERS = 2;
const ADDRESS_SEARCH_DELAY_MS = 250;
const ADDRESS_SEARCH_MAX_RESULTS = 5;

export const DESIGN_TOP_COUNTRIES = ['Canada', 'United States'];
export const DESIGN_COUNTRIES = DESIGN_TOP_COUNTRIES.concat([
    'Afghanistan', 'Albania', 'Algeria', 'Argentina', 'Armenia', 'Australia', 'Austria', 'Bahamas', 'Bangladesh', 'Barbados', 'Belgium', 'Belize',
    'Bermuda', 'Bolivia', 'Brazil', 'Bulgaria', 'Cambodia', 'Chile', 'China', 'Colombia', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic',
    'Denmark', 'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador', 'Estonia', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Germany', 'Ghana', 'Greece',
    'Guatemala', 'Haiti', 'Honduras', 'Hong Kong', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Jamaica',
    'Japan', 'Jordan', 'Kenya', 'Kuwait', 'Latvia', 'Lebanon', 'Lithuania', 'Luxembourg', 'Malaysia', 'Malta', 'Mexico', 'Morocco', 'Nepal',
    'Netherlands', 'New Zealand', 'Nicaragua', 'Nigeria', 'Norway', 'Pakistan', 'Panama', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal',
    'Puerto Rico', 'Qatar', 'Romania', 'Saudi Arabia', 'Serbia', 'Singapore', 'Slovakia', 'Slovenia', 'South Africa', 'South Korea', 'Spain',
    'Sri Lanka', 'Sweden', 'Switzerland', 'Taiwan', 'Thailand', 'Trinidad and Tobago', 'Turkey', 'Ukraine', 'United Arab Emirates',
    'United Kingdom', 'Uruguay', 'Venezuela', 'Vietnam',
]);

// Design matching: the country starts with the typed text (case-insensitive).
export const filterDesignCountries = (query: string) => {
    const q = query.trim().toLowerCase();
    if (!q) return DESIGN_COUNTRIES;
    return DESIGN_COUNTRIES.filter((country) => country.toLowerCase().startsWith(q));
};

const fieldStyle = (error?: boolean): React.CSSProperties => ({
    fontFamily: "'DM Sans', sans-serif", fontSize: 14, border: `1px solid ${error ? RED : BORDER}`, borderRadius: 8,
    padding: '11px 12px', color: TEXT, outline: 'none', width: '100%', boxSizing: 'border-box', background: '#FFFFFF',
});

const popupStyle: React.CSSProperties = {
    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#FFFFFF', border: `1px solid ${BORDER}`,
    borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.08)', zIndex: 30, overflow: 'hidden', display: 'flex', flexDirection: 'column',
};

type Suggestion = { id: string; street: string; line2: string; prediction: GooglePlacePrediction };

const splitPrediction = (prediction: GooglePlacePrediction): { street: string; line2: string } => {
    const structured = prediction as unknown as { mainText?: { toString(): string }; secondaryText?: { toString(): string } };
    const main = structured.mainText?.toString().trim();
    if (main) return { street: main, line2: structured.secondaryText?.toString().trim() || '' };
    const full = prediction.text.toString();
    const comma = full.indexOf(',');
    return comma > -1 ? { street: full.slice(0, comma).trim(), line2: full.slice(comma + 1).trim() } : { street: full, line2: '' };
};

/** Design street field with Google address suggestions. Manual entry always works. */
export const CmpStreetAddressInput = ({ value, onChange, onAddressSelect, placeholder, ariaLabel, name, error, helperText, disabled }: {
    value: string;
    onChange: (value: string) => void;
    onAddressSelect: (address: SelectedGoogleAddress) => void;
    placeholder: string;
    ariaLabel: string;
    name?: string;
    error?: boolean;
    helperText?: string;
    disabled?: boolean;
}) => {
    const [query, setQuery] = useState('');
    const [items, setItems] = useState<Suggestion[]>([]);
    const [open, setOpen] = useState(false);
    const [none, setNone] = useState(false);
    const [highlight, setHighlight] = useState(0);
    const [notice, setNotice] = useState('');
    const sequence = useRef(0);
    const session = useRef<object | undefined>(undefined);
    const mounted = useRef(true);
    const listId = useRef(`cmp-address-${Math.random().toString(36).slice(2)}`).current;
    const configured = isGoogleAddressAutocompleteConfigured();

    useEffect(() => { mounted.current = true; return () => { mounted.current = false; sequence.current += 1; }; }, []);

    useEffect(() => {
        const request = ++sequence.current;
        if (!configured || disabled || query.trim().length < ADDRESS_SEARCH_MIN_CHARACTERS || value !== query) {
            setItems([]); setNone(false); return;
        }
        const timer = window.setTimeout(async () => {
            try {
                const places = await loadGoogleAddressPlaces();
                if (!mounted.current || request !== sequence.current) return;
                session.current = session.current || new places.AutocompleteSessionToken();
                const result = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                    input: query, sessionToken: session.current, includedRegionCodes: GOOGLE_ADDRESS_REGION_CODES,
                });
                if (!mounted.current || request !== sequence.current) return;
                const next = result.suggestions.flatMap((item) => item.placePrediction
                    ? [{ id: item.placePrediction.placeId, prediction: item.placePrediction, ...splitPrediction(item.placePrediction) }] : [])
                    .slice(0, ADDRESS_SEARCH_MAX_RESULTS);
                setItems(next); setNone(next.length === 0); setHighlight(0); setOpen(true);
            } catch {
                if (mounted.current && request === sequence.current) {
                    setItems([]); setNone(false); setNotice('Address search is unavailable. You can enter the address manually.');
                }
            }
        }, ADDRESS_SEARCH_DELAY_MS);
        return () => window.clearTimeout(timer);
    }, [query, value, configured, disabled]);

    const pick = async (item: Suggestion) => {
        const request = ++sequence.current;
        session.current = undefined;
        setOpen(false); setItems([]); setNone(false); setQuery('');
        try {
            const place = item.prediction.toPlace();
            await place.fetchFields({ fields: ['addressComponents'] });
            if (!mounted.current || request !== sequence.current) return;
            const address = mapGoogleAddress(place.addressComponents);
            if (!address) { setNotice('This result has no street address. Enter the address manually.'); return; }
            // A selection replaces the whole address; missing parts stay empty for review.
            onAddressSelect(address);
            setNotice('Check the address and add a unit number if needed.');
        } catch {
            if (mounted.current && request === sequence.current) setNotice('The address could not be loaded. Your other address fields are unchanged.');
        }
    };

    const showPopup = open && (items.length > 0 || none);
    return (
        <div style={{ position: 'relative' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={GRAY_600} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ position: 'absolute', left: 12, top: 12, pointerEvents: 'none' }} aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
            <input
                name={name} value={value} placeholder={placeholder} autoComplete="off" disabled={disabled}
                aria-label={ariaLabel} aria-invalid={error || undefined} role="combobox" aria-autocomplete="list"
                aria-expanded={showPopup} aria-controls={showPopup ? listId : undefined}
                aria-activedescendant={showPopup && items[highlight] ? `${listId}-${highlight}` : undefined}
                onChange={(event) => { onChange(event.target.value); setQuery(event.target.value); setNotice(''); setOpen(true); }}
                onFocus={() => setOpen(true)}
                onBlur={() => window.setTimeout(() => { if (mounted.current) setOpen(false); }, 120)}
                onKeyDown={(event) => {
                    if (event.key === 'Escape') { setOpen(false); return; }
                    if (!showPopup || !items.length) return;
                    if (event.key === 'ArrowDown') { event.preventDefault(); setHighlight((index) => (index + 1) % items.length); }
                    else if (event.key === 'ArrowUp') { event.preventDefault(); setHighlight((index) => (index - 1 + items.length) % items.length); }
                    else if (event.key === 'Enter') { event.preventDefault(); void pick(items[highlight]); }
                }}
                style={{ ...fieldStyle(error), padding: '11px 12px 11px 38px' }}
            />
            {showPopup && (
                <div id={listId} role="listbox" aria-label="Address suggestions" style={popupStyle}>
                    {items.map((item, index) => (
                        <div key={item.id} id={`${listId}-${index}`} role="option" aria-selected={index === highlight}
                            onMouseDown={(event) => { event.preventDefault(); void pick(item); }} onMouseEnter={() => setHighlight(index)}
                            style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', cursor: 'pointer', background: index === highlight ? HIGHLIGHT : '#FFFFFF' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true"><path d="M12 22s7-6.2 7-12a7 7 0 0 0-14 0c0 5.8 7 12 7 12z" /><circle cx="12" cy="10" r="2.5" /></svg>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: TEXT }}>{item.street}</div>
                                {item.line2 && <div style={{ fontSize: 13, color: GRAY_600 }}>{item.line2}</div>}
                            </div>
                        </div>
                    ))}
                    {none && <div style={{ padding: '12px 14px', fontSize: 13, color: GRAY_600 }}>No matching addresses. Keep typing to enter the address manually.</div>}
                    {/* Google requires its attribution next to Places suggestions shown without a map. */}
                    {items.length > 0 && <div style={{ padding: '6px 14px 8px' }}><img src={`${process.env.PUBLIC_URL || ''}/google-maps-attribution.svg`} alt="Google Maps" height={14} /></div>}
                </div>
            )}
            {(error && helperText) && <div style={{ color: RED, fontSize: 12, marginTop: 4 }}>{helperText}</div>}
            {notice && <div role="status" style={{ color: GRAY_600, fontSize: 12, marginTop: 4 }}>{notice}</div>}
        </div>
    );
};

/** Design Country dropdown: "Select Country", Canada and United States first, starts-with search. */
export const CmpCountrySelect = ({ value, onChange, ariaLabel = 'Country', error, helperText, disabled }: {
    value: string; onChange: (country: string) => void; ariaLabel?: string; error?: boolean; helperText?: string; disabled?: boolean;
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [highlight, setHighlight] = useState(0);
    const listId = useRef(`cmp-country-${Math.random().toString(36).slice(2)}`).current;
    const current = (value || '').trim();
    // A saved value outside the list (older free-text records) is shown as is and never lost.
    const known = DESIGN_COUNTRIES.find((country) => country.toLowerCase() === current.toLowerCase());
    const items = filterDesignCountries(search);
    const choose = (country: string) => { onChange(country); setOpen(false); setSearch(''); };
    const toggle = () => {
        if (disabled) return;
        setOpen((wasOpen) => !wasOpen);
        setSearch('');
        const index = DESIGN_COUNTRIES.findIndex((country) => country === known);
        setHighlight(index > -1 ? index : 0);
    };
    const keys = (event: React.KeyboardEvent) => {
        if (event.key === 'Escape') { setOpen(false); return; }
        if (!open) { if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') { event.preventDefault(); toggle(); } return; }
        if (!items.length) return;
        if (event.key === 'ArrowDown') { event.preventDefault(); setHighlight((index) => (index + 1) % items.length); }
        else if (event.key === 'ArrowUp') { event.preventDefault(); setHighlight((index) => (index - 1 + items.length) % items.length); }
        else if (event.key === 'Enter') { event.preventDefault(); choose(items[highlight]); }
    };
    return (
        <div style={{ position: 'relative' }} onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false); }}>
            <input readOnly value={known || current} placeholder="Select Country" aria-label={ariaLabel} disabled={disabled}
                role="combobox" aria-expanded={open} aria-controls={open ? listId : undefined} aria-invalid={error || undefined}
                onMouseDown={(event) => { event.preventDefault(); toggle(); }} onKeyDown={keys}
                style={{ ...fieldStyle(error), padding: '11px 40px 11px 12px', cursor: disabled ? 'default' : 'pointer' }} />
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={GRAY_600} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ position: 'absolute', right: 12, top: 12, pointerEvents: 'none' }} aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
            {open && (
                <div style={popupStyle}>
                    <input autoFocus value={search} placeholder="Search Country" aria-label="Search Country" onKeyDown={keys}
                        onChange={(event) => { setSearch(event.target.value); setHighlight(0); }}
                        style={{ ...fieldStyle(), border: 'none', borderBottom: `1px solid ${BORDER}`, borderRadius: 0 }} />
                    <div id={listId} role="listbox" aria-label="Countries" style={{ maxHeight: 240, overflowY: 'auto' }}>
                        {items.map((country, index) => (
                            <div key={country} role="option" aria-selected={country === known} tabIndex={-1}
                                onMouseDown={(event) => { event.preventDefault(); choose(country); }} onMouseEnter={() => setHighlight(index)}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 14px', fontSize: 14,
                                    color: TEXT, cursor: 'pointer', fontWeight: country === known ? 600 : 400,
                                    background: index === highlight ? HIGHLIGHT : country === known ? SELECTED : '#FFFFFF' }}>
                                {country}
                                {country === known && <svg viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                                    style={{ width: 16, height: 16, flexShrink: 0 }} aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>}
                            </div>
                        ))}
                        {!items.length && <div style={{ padding: '12px 14px', fontSize: 13, color: GRAY_600 }}>No matching countries.</div>}
                    </div>
                </div>
            )}
            {(error && helperText) && <div style={{ color: RED, fontSize: 12, marginTop: 4 }}>{helperText}</div>}
        </div>
    );
};
